// R115 calendar hub — the TASK entity: chip kind (opens the panel; desktop drag
// = new due date), the inspector panel (summary + "Open in Tasks" + Mark done /
// Change due date). Writes: POST /api/tasks (the Tasks module's upsert — a
// partial update applies only the keys sent; title is required), so its
// activity log / mentions fire like any other edit.
import { registerInspector, registerSection, registerAction, registerChipKind, type CalendarCtx } from '../registry';
import type { TaskItem } from '../../../../../lib/calendarOps.mjs';
import { OFFICE, views, findTask, propName, postJson, busy, pill, oneShot } from './util';

const ALL_VIEWS = views('stays', 'ops', 'ops.hk', 'ops.maint', 'guests', 'mtr', 'health');
const T = (d: unknown) => d as TaskItem | null;
const PRIO_TONE: Record<string, string> = { urgent: 'bad', high: 'warn', normal: 'info', low: 'muted' };
const STATUS: Record<string, string> = { todo: 'To do', in_progress: 'In progress', blocked: 'Blocked', done: 'Done', canceled: 'Canceled' };

async function update(ctx: CalendarCtx, t: TaskItem, patch: Record<string, unknown>, msg: string): Promise<boolean> {
  // R115 review: a PARTIAL update — never the layer's title (clipped to 120 chars, it cut long titles and logged a rename)
  const r = await busy(() => postJson('/api/tasks', { id: t.id, ...patch, source: 'calendar' }));
  if (!r.ok) { ctx.toast(r.error); return false; }
  ctx.toast(msg);
  await ctx.reload({ layers: ['tasks'], inspector: true });
  return true;
}
const moveDue = (ctx: CalendarCtx, t: TaskItem, date: string) => {
  if (date < ctx.asOf) { ctx.toast('Pick today or a later day.'); return Promise.resolve(false); }
  return update(ctx, t, { due_date: date }, `Due ${ctx.fmt.wmd(date)}`);
};

registerInspector({
  kind: 'task',
  title: (ref, data) => (T(data) ? T(data)!.title : 'Task'),
  sub: (ref, data, ctx) => { const t = T(data); return t ? `Task · ${propName(ctx, t.listing_id)} · due ${ctx.fmt.wmd(t.due_date)}` : 'Task'; },
  load: async (ref, _s, ctx) => findTask(ctx, ref.id),
});
registerSection({
  inspector: 'task', key: 'task_summary', title: 'Task', order: 10, openIn: ALL_VIEWS,
  when: () => true,
  render: (ref, data, ctx) => {
    const t = T(data);
    if (!t) return `<div class="opv-note">This task isn't in view any more (done, moved or outside the loaded days). <button type="button" class="btn-ghost btn-sm" data-opv-open-task>Open Tasks</button></div>`;
    const kv = (k: string, v: string) => `<div class="opv-kv"><span>${ctx.esc(k)}</span><b>${v}</b></div>`;
    return `<div class="opv-sum"><div class="opv-pills">${pill(ctx, STATUS[t.status] || t.status, t.status === 'blocked' ? 'warn' : 'info')} ${pill(ctx, t.priority, PRIO_TONE[t.priority] || 'info')}${t.overdue ? ` ${pill(ctx, 'Overdue', 'bad')}` : ''}</div>`
      + kv('Due', `${ctx.esc(ctx.fmt.wmd(t.due_date))}${t.due_time ? ` · ${ctx.esc(t.due_time)}` : ''}`)
      + kv('Home', ctx.esc(propName(ctx, t.listing_id)))
      + kv('Assigned', t.assignees.length ? ctx.esc(t.assignees.map(a => a.name).join(', ')) : '<span class="opv-muted">nobody</span>')
      + (t.checklist ? kv('Checklist', `${t.checklist.done}/${t.checklist.count}`) : '')
      + (t.link_kind ? kv('Linked to', ctx.esc(t.link_kind === 'wo' ? 'a work order' : t.link_kind === 'reservation' ? 'a reservation' : 'an issue')) : '')
      + `<p class="opv-muted">Comments, mentions and the full history live on the Tasks page.</p></div>`;
  },
  bind: (root, ref, data, ctx) => {
    root.querySelector('[data-opv-open-task]')?.addEventListener('click', () => { oneShot('tk_focus_task', ref.id); ctx.go('tasks'); });
  },
});
registerAction({
  key: 'task_open', inspector: 'task', label: 'Open in Tasks', icon: '↗', order: 10, roles: OFFICE, primaryIn: ALL_VIEWS,
  when: () => true,
  run: (ref, data, ctx) => { oneShot('tk_focus_task', ref.id); ctx.go('tasks'); },
});
registerAction({
  key: 'task_done', inspector: 'task', label: 'Mark done', icon: '✓', order: 20, roles: OFFICE,
  when: (ref, data) => { const t = T(data); return !!t && t.status !== 'done' && t.status !== 'canceled'; },
  run: (ref, data, ctx) => { const t = T(data); if (t) void update(ctx, t, { status: 'done' }, 'Task done'); },
});
registerAction({
  key: 'task_move', inspector: 'task', label: 'Change due date…', icon: '📅', order: 30, roles: OFFICE,
  when: (ref, data) => !!T(data),
  run: (ref, data, ctx) => {
    const t = T(data);
    if (!t) return;
    if (!t.listing_id) {   // the 21-day strip is per home; an office task picks from a plain date field in the sheet
      const root = ctx.openSheet(`<div class="opv-sheet"><div class="opv-sheet-body"><label class="opv-field"><span>New due date</span><input type="date" min="${ctx.asOf}" value="${ctx.esc(t.due_date >= ctx.asOf ? t.due_date : ctx.asOf)}" data-opv-date></label></div><div class="opv-sheet-foot" data-bottom-bar><button type="button" class="btn-secondary" data-opv-cancel>Cancel</button><button type="button" class="btn-primary" data-opv-save>Save</button></div></div>`, { title: 'Change due date' });
      root.querySelector('[data-opv-cancel]')?.addEventListener('click', () => ctx.closeSheet());
      root.querySelector('[data-opv-save]')?.addEventListener('click', async () => {
        const v = (root.querySelector('[data-opv-date]') as HTMLInputElement).value;
        if (v && await moveDue(ctx, t, v)) ctx.closeSheet();
      });
      return;
    }
    ctx.pickDay({ listing_id: t.listing_id, label: `New due date for “${t.title.slice(0, 40)}”`, allowTaken: true, onPick: (d) => { void moveDue(ctx, t, d); } });
  },
});
registerChipKind({
  kind: 'task', legend: 'Task',
  open: (ref, _anchor, ctx) => ctx.openInspector(ref),
  move: {
    canDrop: (ref, date, ctx) => !!findTask(ctx, ref.id) && date >= ctx.asOf,
    onDrop: async (ref, date, ctx) => { const t = findTask(ctx, ref.id); if (t && t.due_date !== date) await moveDue(ctx, t, date); },
  },
});
